import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, RefreshCw, AlertCircle, Mail, Filter, RotateCcw, Plus, X } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { AdminTicket, TicketCounts, TicketStatus, TicketPriority, Product } from '@/types';

// ─── Filter types ─────────────────────────────────────────────────────────────

type AssignFilter = 'me' | 'unassigned' | 'others';

interface Filters {
  statuses:   Set<TicketStatus>;
  assignment: Set<AssignFilter>;
  priorities: Set<TicketPriority>;
}

/** Default view: active work queue — new + open + pending, assigned to me or unassigned */
const DEFAULT_FILTERS: Filters = {
  statuses:   new Set<TicketStatus>(['new', 'open', 'pending']),
  assignment: new Set<AssignFilter>(['me', 'unassigned']),
  priorities: new Set<TicketPriority>(),  // empty = all priorities
};

const FILTER_KEY = 'tickets_filter_v2';

function serializeFilters(f: Filters): string {
  return JSON.stringify({
    statuses:   [...f.statuses],
    assignment: [...f.assignment],
    priorities: [...f.priorities],
  });
}

function deserializeFilters(raw: string): Filters | null {
  try {
    const obj = JSON.parse(raw);
    return {
      statuses:   new Set<TicketStatus>(obj.statuses   ?? []),
      assignment: new Set<AssignFilter>(obj.assignment ?? []),
      priorities: new Set<TicketPriority>(obj.priorities ?? []),
    };
  } catch { return null; }
}

function loadFilters(): Filters {
  try {
    const raw = localStorage.getItem(FILTER_KEY);
    if (raw) return deserializeFilters(raw) ?? cloneDefaults();
  } catch { /* ignore */ }
  return cloneDefaults();
}

function cloneDefaults(): Filters {
  return {
    statuses:   new Set(DEFAULT_FILTERS.statuses),
    assignment: new Set(DEFAULT_FILTERS.assignment),
    priorities: new Set<TicketPriority>(),
  };
}

function saveFilters(f: Filters): void {
  try { localStorage.setItem(FILTER_KEY, serializeFilters(f)); } catch { /* ignore */ }
}

function eqSets<T>(a: Set<T>, b: Set<T>): boolean {
  return a.size === b.size && [...a].every((x) => b.has(x));
}

// ─── Client-side filter logic ─────────────────────────────────────────────────

function applyFilters(tickets: AdminTicket[], filters: Filters, myName: string): AdminTicket[] {
  return tickets.filter((t) => {
    // Status: empty set = show all
    if (filters.statuses.size > 0 && !filters.statuses.has(t.status)) return false;

    // Assignment: empty set = show all
    if (filters.assignment.size > 0) {
      const isMe         = !!myName && t.assigned_to_name === myName;
      const isUnassigned = !t.assigned_to_name;
      const isOther      = !!t.assigned_to_name && (!myName || t.assigned_to_name !== myName);
      const passes =
        (filters.assignment.has('me')         && isMe)         ||
        (filters.assignment.has('unassigned') && isUnassigned) ||
        (filters.assignment.has('others')     && isOther);
      if (!passes) return false;
    }

    // Priority: empty set = show all
    if (filters.priorities.size > 0 && !filters.priorities.has(t.priority ?? 'high')) return false;

    return true;
  });
}

// ─── Badge style maps ─────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, string> = {
  new:      'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400',
  open:     'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  pending:  'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  closed:   'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  resolved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
};

const PRIORITY_BADGE: Record<string, string> = {
  low:    'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  medium: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  high:   'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
  urgent: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
};

const ASSIGN_BADGE: Record<AssignFilter, string> = {
  me:         'bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400',
  unassigned: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  others:     'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── Chip toggle ──────────────────────────────────────────────────────────────

function ToggleChip<T extends string>({
  value, active, label, activeClass, onToggle,
}: {
  value: T;
  active: boolean;
  label: string;
  activeClass: string;
  onToggle: (v: T) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(value)}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all select-none ${
        active
          ? `${activeClass} border-transparent ring-1 ring-inset ring-current/20`
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Divider ─────────────────────────────────────────────────────────────────

function VDivider() {
  return <div className="hidden sm:block w-px self-stretch bg-gray-100 dark:bg-gray-700 flex-shrink-0" />;
}

// ─── New Ticket Modal ─────────────────────────────────────────────────────────

function NewTicketModal({
  products,
  onClose,
  onCreated,
}: {
  products: Product[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [userEmail,   setUserEmail]   = useState('');
  const [userName,    setUserName]    = useState('');
  const [subject,     setSubject]     = useState('');
  const [description, setDescription] = useState('');
  const [priority,    setPriority]    = useState<TicketPriority>('medium');
  const [productId,   setProductId]   = useState<number>(0);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [sent,        setSent]        = useState(false);

  // Close on backdrop click
  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.createAdminTicket({
        user_email:  userEmail.trim(),
        user_name:   userName.trim(),
        subject:     subject.trim(),
        description: description.trim(),
        priority,
        product_id:  productId || undefined,
      });
      setSent(true);
      setTimeout(() => { onCreated(); }, 2000);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to send.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 p-4"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">New Support Ticket</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Success state */}
        {sent && (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Mail size={22} className="text-green-600 dark:text-green-400" />
            </div>
            <p className="font-semibold text-gray-900 dark:text-white">Email sent to customer</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              A ticket will be created automatically when the customer replies.
            </p>
          </div>
        )}

        {/* Form — wraps scrollable body AND footer so the submit button works */}
        {!sent && <form id="new-ticket-form" onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-y-auto px-6 py-5">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">
              <AlertCircle size={14} className="flex-shrink-0" /> {error}
            </div>
          )}

          {/* User email */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              User email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="user@company.com"
              className="input w-full"
            />
          </div>

          {/* User name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              User name
            </label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Full name (optional)"
              className="input w-full"
            />
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief description of the issue"
              className="input w-full"
              maxLength={500}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Full details of the issue…"
              className="input w-full resize-none"
              maxLength={10000}
            />
          </div>

          {/* Priority + Product row */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as TicketPriority)}
                className="input w-full"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {products.length > 0 && (
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Product</label>
                <select
                  value={productId}
                  onChange={(e) => setProductId(Number(e.target.value))}
                  className="input w-full"
                >
                  <option value={0}>— none —</option>
                  {products.filter((p) => p.is_active).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </form>}

        {/* Footer — hidden once email has been sent */}
        {!sent && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="new-ticket-form"
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? 'Creating…' : 'Create ticket'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Tickets() {
  const navigate = useNavigate();
  const myName   = api.getAdminName();

  const [filters, setFilters]       = useState<Filters>(loadFilters);
  const [allTickets, setAllTickets]  = useState<AdminTicket[]>([]);
  const [counts, setCounts]          = useState<TicketCounts | null>(null);
  const [loading, setLoading]        = useState(true);
  const [error, setError]            = useState<string | null>(null);
  const [products, setProducts]      = useState<Product[]>([]);
  const [showNewTicket, setShowNewTicket] = useState(false);

  // Persist filter changes
  useEffect(() => { saveFilters(filters); }, [filters]);

  async function load() {
    setLoading(true); setError(null);
    try {
      // Always fetch all — we filter client-side for multi-select support
      const res = await api.listAdminTickets();
      setAllTickets(res.tickets);
      setCounts(res.counts);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load tickets.');
    } finally { setLoading(false); }
  }

  useEffect(() => {
    load();
    api.listProducts().then(setProducts).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => applyFilters(allTickets, filters, myName),
    [allTickets, filters, myName],
  );

  const isDefault = useMemo(() =>
    eqSets(filters.statuses,   DEFAULT_FILTERS.statuses)   &&
    eqSets(filters.assignment, DEFAULT_FILTERS.assignment) &&
    filters.priorities.size === 0,
  [filters]);

  // ── Toggle helpers ──
  function toggleStatus(s: TicketStatus) {
    setFilters((prev) => {
      const next = new Set(prev.statuses);
      next.has(s) ? next.delete(s) : next.add(s);
      return { ...prev, statuses: next };
    });
  }
  function toggleAssignment(a: AssignFilter) {
    setFilters((prev) => {
      const next = new Set(prev.assignment);
      next.has(a) ? next.delete(a) : next.add(a);
      return { ...prev, assignment: next };
    });
  }
  function togglePriority(p: TicketPriority) {
    setFilters((prev) => {
      const next = new Set(prev.priorities);
      next.has(p) ? next.delete(p) : next.add(p);
      return { ...prev, priorities: next };
    });
  }
  function resetFilters() { setFilters(cloneDefaults()); }

  // ── Active filter summary text ──
  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (filters.statuses.size > 0)
      parts.push([...filters.statuses].map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', '));
    else
      parts.push('All statuses');
    if (filters.assignment.size > 0) {
      const labels: Record<AssignFilter, string> = { me: 'Mine', unassigned: 'Unassigned', others: 'Others' };
      parts.push([...filters.assignment].map((a) => labels[a]).join(', '));
    } else {
      parts.push('All assignments');
    }
    if (filters.priorities.size > 0)
      parts.push([...filters.priorities].map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', '));
    return parts.join(' · ');
  }, [filters]);

  return (
    <div className="max-w-6xl">

      {/* ── New Ticket Modal ── */}
      {showNewTicket && (
        <NewTicketModal
          products={products}
          onClose={() => setShowNewTicket(false)}
          onCreated={() => {
            setShowNewTicket(false);
            load();
          }}
        />
      )}

      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Support Tickets</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">Manage and respond to customer requests</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => setShowNewTicket(true)}
            className="btn-primary"
          >
            <Plus size={15} className="mr-1.5" />
            New ticket
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
          {([
            ['New',      'new',      'text-indigo-600 dark:text-indigo-400'],
            ['Open',     'open',     'text-blue-600 dark:text-blue-400'],
            ['Pending',  'pending',  'text-yellow-600 dark:text-yellow-400'],
            ['Resolved', 'resolved', 'text-green-600 dark:text-green-400'],
            ['Closed',   'closed',   'text-gray-500 dark:text-gray-400'],
          ] as const).map(([label, key, cls]) => (
            <div
              key={key}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
            >
              <div className={`text-2xl font-bold ${cls}`}>{counts[key] ?? 0}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 mb-4">
        {/* Filter groups row */}
        <div className="flex flex-wrap gap-x-1 gap-y-3 items-center">

          {/* Status */}
          <div className="flex items-center gap-2 flex-wrap pr-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 w-12 flex-shrink-0">Status</span>
            {(['new', 'open', 'pending', 'closed', 'resolved'] as TicketStatus[]).map((s) => (
              <ToggleChip
                key={s}
                value={s}
                active={filters.statuses.has(s)}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                activeClass={STATUS_BADGE[s]}
                onToggle={toggleStatus}
              />
            ))}
          </div>

          <VDivider />

          {/* Assignment */}
          <div className="flex items-center gap-2 flex-wrap px-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 w-12 flex-shrink-0">Assign</span>
            <ToggleChip
              value="me"
              active={filters.assignment.has('me')}
              label="Mine"
              activeClass={ASSIGN_BADGE.me}
              onToggle={toggleAssignment}
            />
            <ToggleChip
              value="unassigned"
              active={filters.assignment.has('unassigned')}
              label="Unassigned"
              activeClass={ASSIGN_BADGE.unassigned}
              onToggle={toggleAssignment}
            />
            <ToggleChip
              value="others"
              active={filters.assignment.has('others')}
              label="Others"
              activeClass={ASSIGN_BADGE.others}
              onToggle={toggleAssignment}
            />
          </div>

          <VDivider />

          {/* Priority */}
          <div className="flex items-center gap-2 flex-wrap px-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 w-12 flex-shrink-0">Priority</span>
            {(['low', 'medium', 'high', 'urgent'] as TicketPriority[]).map((p) => (
              <ToggleChip
                key={p}
                value={p}
                active={filters.priorities.has(p)}
                label={p.charAt(0).toUpperCase() + p.slice(1)}
                activeClass={PRIORITY_BADGE[p]}
                onToggle={togglePriority}
              />
            ))}
          </div>

          {/* Reset — appears only when non-default */}
          {!isDefault && (
            <button
              type="button"
              onClick={resetFilters}
              className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 hover:border-red-200 dark:hover:border-red-700 transition-colors"
              title="Reset to defaults"
            >
              <RotateCcw size={11} /> Reset
            </button>
          )}
        </div>

        {/* Result count + active filter summary */}
        <div className="flex items-center gap-2 mt-2.5 pt-2 border-t border-gray-50 dark:border-gray-700/60">
          <Filter size={11} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
          <span className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
            <span className="font-semibold text-gray-600 dark:text-gray-300">{filtered.length}</span>
            {allTickets.length !== filtered.length && (
              <span className="text-gray-400 dark:text-gray-500"> of {allTickets.length}</span>
            )}{' '}
            ticket{filtered.length !== 1 ? 's' : ''} · {filterSummary}
          </span>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400 text-sm mb-4">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
        </div>
      )}

      {/* ── Loading ── */}
      {loading && <div className="text-sm text-gray-500 dark:text-gray-400 py-16 text-center">Loading…</div>}

      {/* ── Empty state ── */}
      {!loading && !error && filtered.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-16 text-center">
          <Inbox size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 dark:text-white mb-1">No tickets match</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {allTickets.length === 0
              ? 'No support tickets yet.'
              : 'Try adjusting your filters above.'}
          </p>
          {!isDefault && (
            <button
              onClick={resetFilters}
              className="mt-3 text-sm text-brand-600 dark:text-brand-400 hover:underline"
            >
              Reset to defaults
            </button>
          )}
        </div>
      )}

      {/* ── Table ── */}
      {!loading && filtered.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  <th className="px-4 py-3 text-left hidden sm:table-cell w-10">#</th>
                  <th className="px-4 py-3 text-left">Subject</th>
                  <th className="px-4 py-3 text-left hidden md:table-cell">Submitted by</th>
                  <th className="px-4 py-3 text-left hidden lg:table-cell">Tenant</th>
                  <th className="px-4 py-3 text-left hidden xl:table-cell">Assigned to</th>
                  <th className="px-4 py-3 text-center hidden sm:table-cell">Status</th>
                  <th className="px-4 py-3 text-right hidden md:table-cell">Replies</th>
                  <th className="px-4 py-3 text-right">Activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {filtered.map((t) => {
                  const isMe = !!myName && t.assigned_to_name === myName;
                  return (
                    <tr
                      key={t.id}
                      onClick={() => navigate(`/tickets/${t.id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    >
                      {/* # */}
                      <td className="px-4 py-3 text-gray-400 dark:text-gray-500 font-mono text-xs hidden sm:table-cell">
                        {t.id}
                      </td>

                      {/* Subject + badges */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-gray-900 dark:text-white truncate max-w-[160px] sm:max-w-xs">
                            {t.subject}
                          </span>
                          {t.source === 'email' && (
                            <span
                              title="Created from email"
                              className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
                            >
                              <Mail size={9} />Email
                            </span>
                          )}
                          {t.product_name && (
                            <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              {t.product_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {/* Mobile-only inline status */}
                          <span className={`sm:hidden inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[t.status] ?? STATUS_BADGE.open}`}>
                            {t.status}
                          </span>
                          {t.user_reply_count > 0 && t.agent_reply_count === 0 && (
                            <span className="text-xs text-orange-500 font-medium">Awaiting reply</span>
                          )}
                        </div>
                      </td>

                      {/* Submitted by */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="text-gray-900 dark:text-white">
                          {t.user_name || '—'}
                          <span className="text-gray-400 dark:text-gray-500 font-mono text-xs ml-1">[{t.user_id}]</span>
                        </div>
                        {t.user_email && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-[180px]">
                            {t.user_email}
                          </div>
                        )}
                      </td>

                      {/* Tenant */}
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hidden lg:table-cell">
                        {t.tenant_name || t.tenant_id}
                      </td>

                      {/* Assigned to */}
                      <td className="px-4 py-3 text-xs hidden xl:table-cell">
                        {t.assigned_to_name ? (
                          <span className={isMe ? 'font-semibold text-brand-600 dark:text-brand-400' : 'text-gray-700 dark:text-gray-300'}>
                            {isMe ? 'Me' : t.assigned_to_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 italic">Unassigned</span>
                        )}
                      </td>

                      {/* Status + priority */}
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[t.status] ?? STATUS_BADGE.open}`}>
                            {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                          </span>
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${PRIORITY_BADGE[t.priority] ?? PRIORITY_BADGE.high}`}>
                            {t.priority ?? 'high'}
                          </span>
                        </div>
                      </td>

                      {/* Replies */}
                      <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400 hidden md:table-cell">
                        {t.reply_count}
                      </td>

                      {/* Activity */}
                      <td className="px-4 py-3 text-right text-gray-400 dark:text-gray-500 text-xs whitespace-nowrap">
                        {relativeTime(t.updated_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
